import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormsServiceService } from '../../../api/services/forms-service.service';
import { environment } from '../../../../environments/environment';
import { CommonService } from '../../../api/services/common.service';
import { FORM_ID } from '../../../helpers/form-id.enum';
import { NavigationStart, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { COLORS } from '../../../helpers/constants/colors.constant';

@Component({
  selector: 'app-dropdown-with-search',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './dropdown-with-search.component.html',
  styleUrl: './dropdown-with-search.component.css',
})
export class DropdownWithSearchComponent {
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  @Input() labelAbove: any;
  @Input() autoFocused: any;
  @Input() twoGridColumn: any;
  @Input() disableFields: boolean = false;
  endpoint = environment.api_endpoint;
  isOpen: boolean = false;
  searchText = '';
  selectedItem: any = null;
  fieldsData: any[] = [];
  allFieldsData: any[] = [];
  searchInput: any;
  formId: any;
  private routerSub!: Subscription;
  private openAnimationFrameId: number | null = null;
  @ViewChild('focusInput') focusInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dropdownPanel', { static: false }) dropdownPanel!: ElementRef;
  @ViewChild('searchInput') searchInputRef!: ElementRef;
  selectedItems: any[] = [];
  openedDirection: 'above' | 'below' | null = null;
  currentPage = 1;
  limit = 20;
  isLoadingMore = false;
  hasMoreData = true;
  constructor(
    private formService: FormsServiceService,
    private elementRef: ElementRef,
    private commonService: CommonService,
    private router: Router,
    private cd: ChangeDetectorRef,
  ) {}
  getControl() {
    if (!this.formGroup) return null;
    const name = this.fields?.fieldName;
    return this.formGroup.controls[name] || this.formGroup.get(name);
  }

  ngOnInit() {
    this.getData();
    document.addEventListener('click', this.handleClickOutside, true);
    this.routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        this.closeDropdown(false);
        this.cd.detectChanges();
      });
    const control = this.getControl();
    if (control) {
      control.valueChanges.subscribe(() => {
        this.syncSelectionFromControl();
        this.cd.detectChanges();
      });
    }
    this.syncSelectionFromControl();
    window.addEventListener('scroll', this.handleScroll, true);
  }

  ngOnChanges() {
    if (!this.autoFocused) return;

    setTimeout(() => {
      this.focusInput?.nativeElement.focus();
    });
  }
  isOptionMatch(opt: any, val: any): boolean {
    if (
      opt === null ||
      opt === undefined ||
      val === null ||
      val === undefined ||
      val === ''
    ) {
      return false;
    }

    if (Array.isArray(val)) {
      if (val.length === 0) return false;
      val = val[0];
      if (val === null || val === undefined || val === '') return false;
    }

    // Direct equality (strings, numbers, or same object reference)
    if (opt === val) return true;

    const isRoleField =
      this.fields?.fieldName === 'role' || this.fields?.fieldName === 'roles';

    // When both are objects
    if (typeof opt === 'object' && typeof val === 'object') {
      // Primary ID matches
      if (opt._id && val._id && String(opt._id) === String(val._id)) return true;
      if (opt.id && val.id && String(opt.id) === String(val.id)) return true;
      if (
        opt.code &&
        val.code &&
        String(opt.code).toLowerCase() === String(val.code).toLowerCase()
      ) {
        return true;
      }
      if (
        opt.regionCode &&
        val.regionCode &&
        String(opt.regionCode).toLowerCase() === String(val.regionCode).toLowerCase()
      ) {
        return true;
      }

      // Email match (for user objects)
      if (
        opt.email &&
        val.email &&
        String(opt.email).toLowerCase() === String(val.email).toLowerCase()
      ) {
        return true;
      }

      // Value match
      if (
        opt.value !== undefined &&
        opt.value !== null &&
        opt.value !== '' &&
        val.value !== undefined &&
        val.value !== null &&
        val.value !== '' &&
        String(opt.value).toLowerCase() === String(val.value).toLowerCase()
      ) {
        return true;
      }

      // Name / label / text match
      if (
        opt.name &&
        val.name &&
        String(opt.name).toLowerCase() === String(val.name).toLowerCase()
      ) {
        return true;
      }
      if (
        opt.text &&
        val.text &&
        String(opt.text).toLowerCase() === String(val.text).toLowerCase()
      ) {
        return true;
      }
      if (
        opt.label &&
        val.label &&
        String(opt.label).toLowerCase() === String(val.label).toLowerCase()
      ) {
        return true;
      }

      // Role match ONLY if it is a role field
      if (isRoleField) {
        const optRole =
          typeof opt.role === 'string'
            ? opt.role
            : opt.role?.role || opt.role?.name || opt.roleTitle || '';
        const valRole =
          typeof val.role === 'string'
            ? val.role
            : val.role?.role || val.role?.name || val.roleTitle || '';
        if (
          optRole &&
          valRole &&
          String(optRole).toLowerCase() === String(valRole).toLowerCase()
        ) {
          return true;
        }
      }

      // Option values extracted via getOptionValue
      const optVal = this.getOptionValue(opt);
      const valVal = this.getOptionValue(val);
      if (
        optVal &&
        valVal &&
        typeof optVal !== 'object' &&
        typeof valVal !== 'object'
      ) {
        if (String(optVal).toLowerCase() === String(valVal).toLowerCase()) {
          return true;
        }
      }

      return false;
    }

    // When val is primitive (or opt is primitive)
    const strVal = String(val).toLowerCase().trim();
    if (
      strVal === '[object object]' ||
      strVal === 'undefined' ||
      strVal === 'null'
    ) {
      return false;
    }

    // Compare with opt's getOptionValue if it's a primitive
    const optValue = this.getOptionValue(opt);
    if (
      optValue !== null &&
      optValue !== undefined &&
      typeof optValue !== 'object'
    ) {
      if (
        optValue === val ||
        String(optValue).toLowerCase().trim() === strVal
      ) {
        return true;
      }
    }

    // If opt is an object, compare primitive val against opt's properties
    if (typeof opt === 'object' && opt !== null) {
      if (opt._id !== undefined && String(opt._id).toLowerCase() === strVal)
        return true;
      if (opt.id !== undefined && String(opt.id).toLowerCase() === strVal)
        return true;
      if (opt.value !== undefined && String(opt.value).toLowerCase() === strVal)
        return true;
      if (opt.email !== undefined && String(opt.email).toLowerCase() === strVal)
        return true;
      if (opt.code !== undefined && String(opt.code).toLowerCase() === strVal)
        return true;
      if (
        opt.regionCode !== undefined &&
        String(opt.regionCode).toLowerCase() === strVal
      )
        return true;
      if (opt.name !== undefined && String(opt.name).toLowerCase() === strVal)
        return true;
      if (opt.text !== undefined && String(opt.text).toLowerCase() === strVal)
        return true;
      if (opt.label !== undefined && String(opt.label).toLowerCase() === strVal)
        return true;
      if (isRoleField) {
        const optRole =
          typeof opt.role === 'string'
            ? opt.role
            : opt.role?.role || opt.role?.name || opt.roleTitle || '';
        if (optRole && String(optRole).toLowerCase() === strVal) return true;
      }
    }

    return false;
  }

  syncSelectionFromControl() {
    const control = this.getControl();
    if (!control) return;

    if (
      this.fields?.multiselect &&
      this.fields?.fieldName !== 'config.region'
    ) {
      const rawVal = control.value;
      this.selectedItems = Array.isArray(rawVal)
        ? rawVal
        : rawVal
          ? (typeof rawVal === 'string' && rawVal.includes(',')
              ? rawVal.split(',').map((v: string) => v.trim())
              : [rawVal])
          : [];
    } else {
      let val = control.value;
      if (Array.isArray(val) && val.length > 0) {
        val = val[0];
      }
      if (
        val !== null &&
        val !== undefined &&
        val !== '' &&
        Array.isArray(this.fieldsData)
      ) {
        this.selectedItem =
          this.fieldsData.find((opt) => this.isOptionMatch(opt, val)) ||
          (val !== '' && val !== null && val !== undefined ? val : null);
      } else {
        this.selectedItem = null;
      }
    }
  }

  handleScroll = (event: Event) => {
    if (!this.isOpen) return;

    const panel = this.dropdownPanel?.nativeElement;
    if (panel?.contains(event.target as Node)) return;

    this.closeDropdown();
  };

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    this.handleClickOutside(event);
  }

  ngOnDestroy() {
    this.closeDropdown(false);
    this.routerSub?.unsubscribe();
    document.removeEventListener('click', this.handleClickOutside, true);
    window.removeEventListener('scroll', this.handleScroll, true);
    // this.getData();
  }

  /** Function to get the data by api or static */
  getData() {
    if (this.fields.options.isStatic === true) {
      this.fieldsData = this.fields.options.staticOptions;
      this.allFieldsData = [...this.fieldsData];
      return;
    } else {
      this.formId = this.fields.options.source.formID;
      const api_endpoint = this.endpoint;
      const method = this.fields.options.source.method;
      let url = this.fields.options.source.apiUrl;

      const replacements = {
        formId: this.formId,
        formID: this.formId,
        api_endpoint: api_endpoint,
      };

      url = this.formService.replaceTemplateTokens(url, replacements);

      const headers = {
        'Content-Type': 'application/json',
      };

      this.currentPage = 1;
      this.hasMoreData = true;
      this.loadData(this.formId, {
        query: this.searchInput,
        page: this.currentPage,
        limit: this.limit,
        sort: 'status',
      });
    }
  }

  loadData(
    formId: any,
    pagination: { query: any; page?: number; limit?: number; sort?: any } = {
      query: this.searchInput,
      page: 1,
      sort: 'status',
    },
  ) {
    if (formId === '68cbc4047cc30f5af305d13b' && pagination) {
      pagination = { ...pagination, sort: 'domain' };
    }
    const paramsOptions =
      formId === '6a0ed7f32db4b8fda5f347a6' ||
      formId === FORM_ID.ACCESS_MGMT_LIST
        ? {
            roleStatus: '=="Active"',
            ...(pagination?.query ? { query: pagination.query } : {}),
          }
        : pagination;
    this.commonService.loadListWithOptions(formId, paramsOptions).subscribe({
      next: (data) => {
        const newRecords = data.data.records || [];
        if (pagination.page === 1) {
          this.fieldsData = newRecords;
        } else {
          this.fieldsData = [...this.fieldsData, ...newRecords];
        }

        if (newRecords.length < this.limit) {
          this.hasMoreData = false;
        }

        this.allFieldsData = [...this.fieldsData];
        this.syncSelectionFromControl();
        this.cd.detectChanges();
        setTimeout(() => {
          this.positionDropdown();
        });
        this.isLoadingMore = false;
      },
      error: (err) => {
        if (pagination.page === 1) {
          this.fieldsData = [];
          this.allFieldsData = [];
        }
        this.isLoadingMore = false;
      },
    });
  }

  onScroll(event: Event) {
    if (this.fields?.options?.isStatic === true) return;

    const target = event.target as HTMLElement;
    if (target.scrollHeight - target.scrollTop - target.clientHeight < 10) {
      this.loadMore();
    }
  }

  loadMore() {
    if (this.isLoadingMore || !this.hasMoreData || !this.formId) return;
    this.isLoadingMore = true;
    this.currentPage++;
    this.loadData(this.formId, {
      query: this.searchInput,
      page: this.currentPage,
      limit: this.limit,
      sort: 'status',
    });
  }
  toggleDropdown(event: MouseEvent) {
    if (
      this.disableFields ||
      this.formGroup.get(this.fields.fieldName)?.disabled
    ) {
      return;
    }
    event.stopPropagation();

    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    const panel = this.dropdownPanel?.nativeElement;
    if (!panel) return;

    this.isOpen = true;

    if (this.openAnimationFrameId !== null) {
      cancelAnimationFrame(this.openAnimationFrameId);
    }

    this.openAnimationFrameId = requestAnimationFrame(() => {
      this.openAnimationFrameId = null;
      if (!this.isOpen) return;

      if (panel.parentNode !== document.body) {
        document.body.appendChild(panel);
      }

      this.openedDirection = null;
      this.positionDropdown();
    });
  }

  positionDropdown() {
    const panel = this.dropdownPanel?.nativeElement;
    if (!panel || !this.isOpen) return;

    this.cd.detectChanges();

    const trigger =
      this.elementRef.nativeElement.querySelector('.dropdown-selected');
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();

    panel.style.display = 'block';
    panel.style.position = 'absolute';

    const panelHeight = panel.offsetHeight;

    // 🔥 Get closest scrollable parent
    const scrollParent = this.getScrollParent(trigger);

    let parentRect;
    if (scrollParent === document.body) {
      parentRect = {
        top: 0,
        bottom: window.innerHeight,
      };
    } else {
      parentRect = scrollParent.getBoundingClientRect();
    }

    const spaceBelow = parentRect.bottom - rect.bottom;
    const spaceAbove = rect.top - parentRect.top;

    panel.style.left = rect.left + window.scrollX + 'px';
    panel.style.width = rect.width + 'px';

    // Determine direction once when opening or reset
    if (!this.openedDirection) {
      const expectedHeight = 210; // Max height of dropdown panel
      if (spaceBelow < expectedHeight && spaceAbove > spaceBelow) {
        this.openedDirection = 'above';
      } else {
        this.openedDirection = 'below';
      }
    }

    // Position using the locked direction
    if (this.openedDirection === 'above') {
      panel.style.top = rect.top + window.scrollY - panelHeight - 2 + 'px';
    } else {
      panel.style.top = rect.bottom + window.scrollY + 'px';
    }
  }

  closeDropdown(resetSearch = true) {
    this.isOpen = false;
    this.openedDirection = null;

    if (this.openAnimationFrameId !== null) {
      cancelAnimationFrame(this.openAnimationFrameId);
      this.openAnimationFrameId = null;
    }

    const panel = this.dropdownPanel?.nativeElement;
    if (panel) {
      panel.style.display = 'none';
      panel.style.visibility = '';

      if (panel.parentNode === document.body) {
        document.body.removeChild(panel);
      }
    }

    if (!resetSearch) return;

    const hadSearch = !!this.searchInput;

    if (this.searchInputRef?.nativeElement) {
      this.searchInputRef.nativeElement.value = '';
    }

    this.searchInput = '';

    if (this.fields?.options?.isStatic === true) {
      this.fieldsData = [...this.allFieldsData];
      return;
    }

    if (this.formId && hadSearch) {
      this.currentPage = 1;
      this.hasMoreData = true;
      this.loadData(this.formId, {
        query: '',
        page: this.currentPage,
        limit: this.limit,
        sort: 'status',
      });
    }
  }

  private getScrollParent(element: HTMLElement): HTMLElement {
    let parent = element.parentElement;

    while (parent) {
      const style = getComputedStyle(parent);
      const overflowY = style.overflowY;

      if (overflowY === 'auto' || overflowY === 'scroll') {
        return parent;
      }

      parent = parent.parentElement;
    }

    return document.body;
  }

  selectOption(option: any) {
    const control = this.getControl();
    if (!control) return;

    const optionValue = this.getOptionValue(option);

    const isMultiselect =
      this.fields?.multiselect && this.fields?.fieldName !== 'config.region';

    if (isMultiselect) {
      let currentValues = control.value || [];

      const exists = currentValues.includes(optionValue);

      if (exists) {
        // ❌ remove
        currentValues = currentValues.filter((v: any) => v !== optionValue);
      } else {
        // ✅ add
        currentValues = [...currentValues, optionValue];
      }

      this.selectedItems = currentValues;

      // ✅ ALWAYS ARRAY
      control.setValue(currentValues, { emitEvent: true });
    } else {
      // ✅ ONLY SINGLE VALUE (not array, not object)
      this.selectedItem = option;

      const isRoleField =
        this.fields?.fieldName === 'role' ||
        this.fields?.fieldName === 'roles';

      const isAssigneeField =
        this.fields?.fieldName === 'assignee' ||
        this.fields?.fieldName === 'assigned';

      const valueToSet =
        this.fields?.fieldName === 'controls' ||
        this.fields?.fieldName === 'location' ||
        isRoleField ||
        isAssigneeField ||
        this.formId === '6a0edc492db4b8fda5f347b7' ||
        this.formId === FORM_ID.CONTROLS_LIST ||
        this.formId === '6a143360ca440a759a89fa08' ||
        this.fields?.formId === '6a143360ca440a759a89fa08' ||
        this.formId === FORM_ID.INVITE_USER ||
        ((this.formId === '6a143253ca440a759a89f9ff' ||
          this.fields?.formId === '6a143253ca440a759a89f9ff' ||
          this.formId === FORM_ID.ADD_USER) &&
          isRoleField)
          ? option
          : (this.formId === '6a143253ca440a759a89f9ff' ||
                this.fields?.formId === '6a143253ca440a759a89f9ff' ||
                this.formId === FORM_ID.ADD_USER) &&
              (this.fields?.fieldName === 'department' ||
                this.fields?.fieldName === 'departments')
            ? Array.isArray(option)
              ? option
              : [option]
            : optionValue;

      control.setValue(valueToSet, { emitEvent: true });
    }

    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity();
    this.cd.detectChanges();

    if (!isMultiselect) {
      this.closeDropdown();
    }
  }
  filterOptions(event: Event) {
    const value = (event.target as HTMLInputElement).value.toLowerCase().trim();

    if (!value) {
      this.fieldsData = [...this.allFieldsData];
      setTimeout(() => {
        this.positionDropdown();
      });
      return;
    }

    this.fieldsData = this.allFieldsData.filter((opt: any) =>
      (opt.text || opt.name || '').toLowerCase().includes(value),
    );
    setTimeout(() => {
      this.positionDropdown();
    });
  }
  @HostListener('window:click', ['$event'])
  closeDropdowns(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown(false);
    }
  }
  handleClickOutside = (event: MouseEvent) => {
    if (!this.isOpen) return;

    const panel = this.dropdownPanel?.nativeElement;
    const trigger = this.focusInput?.nativeElement;

    const clickedInsideTrigger = trigger?.contains(event.target as Node);
    const clickedInsidePanel = panel?.contains(event.target as Node);

    if (clickedInsideTrigger || clickedInsidePanel) {
      return;
    }

    this.closeDropdown();
  };

  onKeyUpSearch(searchInput: any) {
    if (searchInput.length >= 3 || searchInput.length === 0) {
      this.searchInput = searchInput;
      this.currentPage = 1;
      this.hasMoreData = true;
      this.loadData(this.formId, {
        query: this.searchInput,
        page: this.currentPage,
        limit: this.limit,
        sort: 'status',
      });
    }
  }

  formatDisplayName(value: string): string {
    if (!value) return '';

    const uppercaseWords = [
      'api',
      'url',
      'id',
      'ip',
      'dns',
      'sso',
      'saml',
      'oauth',
      'tls',
      'ssl',
    ];
    const originalWords = value.split(' ');

    return value
      .toLowerCase()
      .split(' ')
      .map((word, index) => {
        const originalWord = originalWords[index];
        if (
          originalWord &&
          originalWord === originalWord.toUpperCase() &&
          isNaN(Number(originalWord))
        ) {
          return originalWord;
        }
        const cleanWord = word.replace(/[^a-z0-9]/g, '');
        if (uppercaseWords.includes(cleanWord)) {
          return word.toUpperCase();
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  FORM_ID = FORM_ID;

  getOptionLabel(item: any): string {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (Array.isArray(item)) {
      return item
        .map((i) => this.getOptionLabel(i))
        .filter(Boolean)
        .join(', ');
    }

    if (this.fields?.fieldName === 'config.region') {
      if (item.regionName && item.regionCode) {
        return `${item.regionName} (${item.regionCode})`;
      }
      return item.regionName || item.regionCode || '';
    }

    if (this.formId === '6a3d163923bf07d3d695d993') {
      return item.shortName || item.name || item.text || '';
    }

    if (item.domain && item.fullName) {
      return `${item.domain} (${item.fullName})`;
    }

    if (
      this.formId === '6878d3800453556c08933e90' ||
      this.formId === FORM_ID.USERS ||
      this.fields?.fieldName === 'assignee' ||
      this.fields?.fieldName === 'owners' ||
      this.fields?.fieldName === 'assigned'
    ) {
      return (
        item.name ||
        item.fullName ||
        item.text ||
        item.label ||
        item.email ||
        ''
      );
    }

    const roleStr =
      typeof item.role === 'string'
        ? item.role
        : item.role?.role || item.role?.name || '';
    const mappingHeaderStr =
      typeof item.mappingColumnHeader === 'string'
        ? item.mappingColumnHeader
        : '';

    return (
      item.name ||
      item.text ||
      item.label ||
      roleStr ||
      item.domain ||
      mappingHeaderStr ||
      item.email ||
      item.value ||
      (typeof item.id === 'string' ? item.id : '')
    );
  }

  getSelectedDisplay(): string {
    const control = this.getControl();
    const value = control?.value;

    // 🟢 Multi Select
    if (
      this.fields?.multiselect &&
      this.fields?.fieldName !== 'config.region'
    ) {
      const rawVal = value;
      const valArray = Array.isArray(rawVal)
        ? rawVal
        : rawVal
          ? (typeof rawVal === 'string' && rawVal.includes(',')
              ? rawVal.split(',').map((v: string) => v.trim())
              : [rawVal])
          : [];

      if (valArray.length > 0) {
        const labels = valArray
          .map((val: any) => {
            const strVal = String(val).toLowerCase();
            const matchedOpt = this.fieldsData?.find(
              (opt: any) =>
                this.getOptionValue(opt) === val ||
                String(this.getOptionValue(opt)).toLowerCase() === strVal ||
                opt === val ||
                (opt?._id && opt._id === val) ||
                (opt?.id && opt.id === val) ||
                (opt?.email && opt.email === val) ||
                (opt?.label && String(opt.label).toLowerCase() === strVal) ||
                (opt?.name && String(opt.name).toLowerCase() === strVal) ||
                (opt?.text && String(opt.text).toLowerCase() === strVal) ||
                (opt?.value && String(opt.value).toLowerCase() === strVal),
            );
            return this.getOptionLabel(matchedOpt || val);
          })
          .filter(Boolean);

        if (labels.length === 0) {
          return (
            this.formatPlaceholder(this.fields?.placeholder) || 'Select option'
          );
        }

        if (labels.length <= 3) {
          return labels.join(', ');
        }

        return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
      }

      return (
        this.formatPlaceholder(this.fields?.placeholder) || 'Select option'
      );
    }

    // 🔵 Single Select
    let itemToDisplay: any = this.selectedItem;

    if (
      !itemToDisplay &&
      value !== null &&
      value !== undefined &&
      value !== ''
    ) {
      itemToDisplay =
        this.fieldsData?.find((opt: any) => this.isOptionMatch(opt, value)) ||
        value;
    }

    if (
      itemToDisplay !== null &&
      itemToDisplay !== undefined &&
      itemToDisplay !== ''
    ) {
      const label = this.getOptionLabel(itemToDisplay);
      if (label) return label;
    }

    return this.formatPlaceholder(this.fields?.placeholder) || 'Select option';
  }

  isSelected(option: any): boolean {
    const control = this.getControl();
    if (!control?.value) return false;

    if (
      this.fields?.multiselect &&
      this.fields?.fieldName !== 'config.region'
    ) {
      const rawVals = control.value || [];
      const vals = Array.isArray(rawVals)
        ? rawVals
        : typeof rawVals === 'string' && rawVals.includes(',')
          ? rawVals.split(',').map((v: string) => v.trim())
          : [rawVals];
      return vals.some((v: any) => this.isOptionMatch(option, v));
    }

    return this.isOptionMatch(option, control.value);
  }

  getOptionValue(option: any): any {
    if (!option) return '';
    if (typeof option !== 'object') return option;

    if (this.formId === '6a3d163923bf07d3d695d993') {
      return (
        option?.shortName ||
        option?.name ||
        option?.text ||
        option?.id ||
        option?._id ||
        ''
      );
    }

    // Special form
    if (
      this.formId === '6a0edc492db4b8fda5f347b7' ||
      this.formId === FORM_ID.CONTROLS_LIST ||
      this.fields?.fieldName === 'controls'
    ) {
      return (
        option?.text ||
        option?.name ||
        option?.label ||
        option?.value ||
        option?._id
      );
    }

    if (this.formId === '69c5054ecc0290f4b6634830') {
      return option?.name;
    }

    switch (this.fields?.fieldName) {
      case 'config.region':
        return option?.regionCode ?? option?.id ?? option?._id ?? option?.value;

      case 'category':
        return option?.value ?? option?.id ?? option?._id;

      case 'domains':
        return option?.domain ?? option?.value ?? option?.id;

      case 'owners':
        return option?.email ?? option?.id ?? option?._id;

      case 'department':
        return option?.name ?? option?.id ?? option?.department;

      case 'assignee':
        return (
          option?._id ??
          option?.id ??
          option?.email ??
          option?.name ??
          option
        );

      default:
        return (
          option?.email ??
          option?.domain ??
          option?.id ??
          option?.value ??
          (typeof option?.role === 'string'
            ? option.role
            : option?.role?.role || option?.role?.name) ??
          option?.label ??
          option?.text ??
          option?.mappingColumnHeader ??
          option?._id ??
          option?.name
        );
    }
  }

  hasSelection(): boolean {
    const isMultiselect =
      this.fields?.multiselect && this.fields?.fieldName !== 'config.region';
    if (isMultiselect) {
      return this.selectedItems?.length > 0;
    }
    const control = this.getControl();
    const val = control?.value;
    return (
      !!this.selectedItem ||
      (val !== null &&
        val !== undefined &&
        val !== '' &&
        !(Array.isArray(val) && val.length === 0))
    );
  }

  getSelectedOptionColor(selectedValue: any): string {
    if (!selectedValue) return '#808290';

    // MULTI SELECT
    if (Array.isArray(selectedValue)) {
      if (!selectedValue.length) return '#808290';

      const selectedItems =
        this.fieldsData?.filter((item: any) =>
          selectedValue.some((sv: any) => this.isOptionMatch(item, sv)),
        ) || [];

      if (selectedItems.length) {
        const item = selectedItems[0];
        return this.getColorFromAllMaps(item.label || item.name || item.text);
      }

      return '#808290';
    }

    // SINGLE SELECT
    const selected = this.fieldsData?.find((item: any) =>
      this.isOptionMatch(item, selectedValue),
    );

    if (!selected) return '#808290';

    // ✅ FIX: use same logic as multi-select
    return this.getColorFromAllMaps(
      selected.label || selected.name || selected.text,
    );
  }

  getColorFromAllMaps(label: string): string {
    return COLORS[label?.toLowerCase()] || '#808290';
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.isOpen) {
      this.closeDropdown();
    }
  }

  formatPlaceholder(text: string): string {
    if (!text) return 'Select option';
    return text.replace(/^--|--$/g, '').trim();
  }

  clearSearch(input: HTMLInputElement) {
    input.value = '';
    this.onKeyUpSearch('');
  }

  getSortedOptions() {
    return [
      ...this.fieldsData.filter((o) => this.isSelected(o)),
      ...this.fieldsData.filter((o) => !this.isSelected(o)),
    ];
  }

  isString(val: any): boolean {
    return typeof val === 'string';
  }

  isArray(val: any): boolean {
    return Array.isArray(val);
  }

  // Visual helper methods
  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  }

  getAvatarColor(name: string): string {
    if (!name) return '#808290';
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = [
      '#0052CC', // Blue
      '#00875A', // Green
      '#DE350B', // Red
      '#00B8D9', // Teal
      '#6554C0', // Purple
      '#FF5630', // Orange
      '#36B37E', // Light Green
      '#FFAB00', // Yellow
    ];
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }

  hasValidPhoto(photo: any): boolean {
    if (!photo) return false;
    if (typeof photo !== 'string') return false;
    const trimmed = photo.trim();
    return (
      trimmed !== '' &&
      trimmed !== 'null' &&
      trimmed !== 'undefined' &&
      trimmed !== 'none'
    );
  }

  handleImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = './assets/media/icons/default-user-icon.png';
  }
}
